import { select } from 'd3-selection';
import { line } from 'd3-shape';
import React, { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import {
  CONFIG_ECG,
  calculateConfig,
  calculatePixelSamples,
  calculateSamples,
} from '@/entities/ecg';
import { TEMPLATE_RATIO } from '@/widgets/beats/ui/handlerConstants';
import meanSample from './helper';
import type { TemplatePreviewProps } from './index';

/**
 * Backs a single morphology template thumbnail: its waveform drawing and its reviewed-progress figure. Sizes the preview from the isSmallSize flag, redraws the template's centre vector whenever it or the gain and high-pass change, and derives the reviewed percentage from the template counts adjusted by bulk reviewed markers, their per-cell resets and individual review changes, clamped so a near-complete template never reads as 100.
 *
 * @param props - Template, ECG data map, display flags, selection handler and the reviewed-beat change lists.
 * @returns An object carrying the preview size, the SVG ref, the click handler and the reviewed percentage.
 */
const useTemplatePreview = ({
  onClickSelect,
  gain,
  highPass,
  isSmallSize,
  template,
  isUnclassified,
  ecgDataMap,
  hideStatus,
  selectedTemplate,
  reviewedBeatsChanges,
  bulkReviewedBeats,
  resetBulkReviewedBeats,
}: Pick<
  TemplatePreviewProps,
  | 'onClickSelect'
  | 'gain'
  | 'highPass'
  | 'isSmallSize'
  | 'template'
  | 'isUnclassified'
  | 'ecgDataMap'
  | 'hideStatus'
  | 'selectedTemplate'
  | 'reviewedBeatsChanges'
  | 'bulkReviewedBeats'
  | 'resetBulkReviewedBeats'
>) => {
  const size = isSmallSize ? 98 : 146;
  const canvasRef = useRef<SVGSVGElement>(null);

  /**
   * Selects this template when its thumbnail is clicked. Prevents the default browser action and hands the template to the overview's selection handler, which also rewinds paging and clears bulk actions.
   *
   * @param e - Click event on the thumbnail.
   * @returns Nothing.
   */
  const onClick = (e: React.MouseEvent) => {
    // e.stopPropagation();
    e.preventDefault();
    onClickSelect(template);
  };

  /**
   * Renders one morphology template's averaged ECG waveform into the preview SVG. Builds a draw config from the gain, high-pass and sampling frequency scaled to the preview size, converts the raw signal into pixel samples, recentres them with meanSample, then wipes the SVG and appends a dark stroked path per series.
   *
   * @param ecgData - Averaged ECG signal of the template being previewed.
   * @returns Nothing.
   */
  const drawStrip = ({ ecgData }: { ecgData: any }) => {
    const drawEcgData = [ecgData];
    const config = calculateConfig({
      pGain: gain,
      pSpeed: highPass || 1,
      width: size * TEMPLATE_RATIO.WIDTH,
      height: size * TEMPLATE_RATIO.HEIGHT,
      adcGain: CONFIG_ECG.gain,
      ecgData: drawEcgData,
      samplingRate: ecgDataMap.samplingFrequency,
      pLFF: 1,
    });
    const samples = calculateSamples(drawEcgData, config, true);
    const pixel = calculatePixelSamples(samples, config);
    const pixelSamples = meanSample(pixel[0], size);

    if (canvasRef.current) {
      const svg = select(canvasRef.current);
      svg.selectAll('*').remove();
      const lineFunc = line<{ x: number; y: number }>()
        .x((d) => d.x)
        .y((d) => d.y);
      pixelSamples.forEach((displayedSamples: any) => {
        svg
          .append('path')
          .attr('d', lineFunc(displayedSamples))
          .attr('stroke', '#1D2F47')
          .attr('stroke-width', '1px')
          .attr('fill', 'none');
      });
    }
  };

  useEffect(() => {
    drawStrip({
      ecgData: template.centerVector,
    });
  }, [template.centerVector, gain, highPass]);

  const percentReviewed = useMemo(() => {
    if (_.isEmpty(selectedTemplate) || isUnclassified || hideStatus) return 0;
    const totalBeats = selectedTemplate.total || 0;
    let reviewedCount = selectedTemplate.reviewed || 0;
    _.forEach(bulkReviewedBeats, (item) => {
      if (item.originalTemplateId && item.originalTemplateId === selectedTemplate.id) {
        const originalReviewed = item.originalReviewedCount || 0;
        const totalCount = item.totalCount || 0;
        if (item.isReviewed) {
          reviewedCount += totalCount - originalReviewed;
        } else {
          reviewedCount -= originalReviewed;
        }
        const filterReset = _.filter(
          resetBulkReviewedBeats,
          (x) => x.originalTemplateId === selectedTemplate.id && x.cellId === item.cellId,
        );
        if (filterReset.length > 0) {
          if (item.isReviewed) {
            reviewedCount -= filterReset.length;
          } else {
            reviewedCount += filterReset.length;
          }
        }
      }
    });
    _.forEach(reviewedBeatsChanges, (item) => {
      if (item.originalTemplateId === selectedTemplate.id) {
        if (item.isReviewed) {
          reviewedCount += 1;
        } else {
          reviewedCount -= 1;
        }
      }
    });
    const percent = totalBeats === 0 ? 0 : (reviewedCount / totalBeats) * 100;
    return percent > 99.9 && percent < 100
      ? 99.9
      : percent === 0
        ? 0
        : percent < 0.1
          ? 0.09
          : percent > 100
            ? 100
            : percent.toFixed(1);
  }, [
    selectedTemplate,
    reviewedBeatsChanges,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
    isUnclassified,
    hideStatus,
  ]);

  return {
    size,
    canvasRef,
    onClick,
    percentReviewed,
  };
};

export default useTemplatePreview;
